import { Link } from "@mui/material";
import { TbShirtSport } from "react-icons/tb";
import { HiOutlineTicket } from "react-icons/hi";
import { MdOutlineEventSeat, MdOutlinedFlag } from "react-icons/md";
import Head from "next/head";
import { TeamConfig } from "@/global/hooks/useTeamConfig";
import constants from "@/global/config/constants";
import { MLBAdobeScriptHeader } from "@/global/common/mlb-common/MLBAdobeScriptHeader";

export const howItWorks = [
  {
    icon: <MdOutlinedFlag />,
    title: "Choose your games.",
    description: "Select the games you wish to attend.",
  },
  {
    icon: <MdOutlineEventSeat />,
    title: "Login to MLB.com",
    description: (
      <>
        Click Continue and connect your Texas Rangers account using our secure{" "}
        <Link target="_blank" href="https://www.MLB.com/">
          MLB.com
        </Link>{" "}
        login.
      </>
    ),
  },
  {
    icon: <TbShirtSport />,
    title: "Select Your Seats",
    description: "Select the seats you wish to purchase for each game.",
  },

  {
    icon: <HiOutlineTicket />,
    title: "Get Your Tickets",
    description:
      "Tickets will be available in your Texas Rangers account. Reach out to Live Fan Support for any questions.",
  },
];

export const faqs = [
  {
    title: "When will I receive my tickets?",
    description:
      "If your reservation is accepted, a Texas Rangers representative will be in contact with further information, including ticket delivery details.",
  },
  {
    title: "Is re-entry allowed?",
    description:
      "Re-entry is allowed only at specific gate locations. Before exiting the stadium, please confirm which gates allow re-entry with a Texas Rangers guest services representative.",
  },
  {
    title: "What is included in my suite?",
    description:
      "If your reservation is accepted, a Texas Rangers representative will be in contact with further information, including details regarding parking and food and beverage options.",
  },
];

export function TexasRangersCheckoutTerms() {
  return (
    <>
      In connection with your reservation, you will receive ticket-related
      commercial emails from{" "}
      <Link
        target="_blank"
        sx={{
          fontWeight: 500,
          color: "primary.main",
          textDecoration: "none",
        }}
        href="http://texasrangers.com/"
      >
        texasrangers.com
      </Link>
      . You may unsubscribe at any time. By checking this box you agree to
      receive additional commercial emails from{" "}
      <Link
        target="_blank"
        sx={{
          fontWeight: 500,
          color: "primary.main",
          textDecoration: "none",
        }}
        href="http://texasrangers.com/"
      >
        texasrangers.com
      </Link>{" "}
      and{" "}
      <Link
        target="_blank"
        href="http://MLB.com"
        sx={{
          fontWeight: 500,
          color: "primary.main",
          textDecoration: "none",
        }}
      >
        MLB.com | The Official Site of Major League Baseball
      </Link>
      . The terms and conditions of your ticket and policies applicable to its
      use are available online at{" "}
      <Link
        target="_blank"
        sx={{
          fontWeight: 500,
          color: "primary.main",
          textDecoration: "none",
        }}
        href="https://www.mlb.com/rangers/tickets/terms-and-conditions"
      >
        Rangers Ticket Terms and Conditions | Texas Rangers
      </Link>
      . By clicking the Purchase Tickets button, you agree to accept all Terms
      and Conditions. Your purchase is not complete until a Texas Rangers
      representative connects with you to complete your purchase. Upon
      completing your purchase, you acknowledge and agree that your credit card
      will be charged. By clicking &apos;Purchase Tickets&apos;, you agree to
      the MLB Privacy Policy, Terms of Use and to receive your ticket(s) via an
      SMS text message to the mobile # associated with your Texas Rangers
      account. Message and data rates may apply. Tickets will be made available
      in the MLB Ballpark mobile application only.{" "}
      <Link
        target="_blank"
        href="http://MLB.com"
        sx={{
          fontWeight: 500,
          color: "primary.main",
          textDecoration: "none",
        }}
      >
        MLB.com | The Official Site of Major League Baseball
      </Link>{" "}
      and supported mobile device required.
    </>
  );
}

export function TexasRangersAdditionalDetailsDisclaimerTC() {
  return (
    <>
      By entering my name, checking the box above and clicking Submit Request, I
      hereby (i) agree to the{" "}
      <Link
        target="_blank"
        href="https://www.mlb.com/rangers/tickets/terms-and-conditions"
      >
        Terms of Use
      </Link>{" "}
      (ii) agree that my reservation will not be complete unless and until I
      receive a notice from the Texas Rangers confirming whether or not my
      reservation has been accepted in accordance with our agreement.
    </>
  );
}

export const AnalyticsHeader = (props: { teamConfig: TeamConfig }) => {
  const { teamSchema } = props.teamConfig;

  return (
    <>
      <MLBAdobeScriptHeader />
      <Head>
        <script
          nonce={process.env.pixelScriptNonce}
          id={`${teamSchema}-meta-pixel-script`}
          defer
          dangerouslySetInnerHTML={{
            __html: `
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '1413100238986958');
              fbq('track', 'PageView');
              console.log('meta pixel page load event fired.');
              window.addEventListener("${constants.customEvents.eventPurchaseSuccess}", function(e) {
                try {
                  const { cartRes, checkoutRes } = e.detail;
                  fbq('track', 'Purchase', {
                    content_name: "${teamSchema}_event_purchase", 
                    value: String(cartRes.allInPrice), 
                    currency: 'USD', 
                    order_id: checkoutRes.orderNumber,
                  });
                  console.log('meta pixel purchase event fired.');
                } catch (error) {
                  console.log('Error in meta pixel purchase event', error); 
                }
              });
            `,
          }}
        />

        <noscript>
          <img
            height="1"
            width="1"
            style={{ display: "none" }}
            src="https://www.facebook.com/tr?id=1413100238986958&ev=PageView&noscript=1"
          />
        </noscript>
      </Head>
    </>
  );
};

export const overrideEventCodesArray = [
  "23361",
  "23362",
  "23363",
  "23364",
  "23366",
  "23367",
  "23368",
  "23369",
  "23370",
  "23371",
  "23377",
  "23378",
  "23379",
  "23380",
];
